/*
 * ContinueWhereYouLeftOff — Stage 15 P1.
 *
 * Auto-detects the user's most recent unfinished thread and offers a single
 * one-tap resume CTA. Picks from:
 *   - last chat conversation (last_activity within 24h)
 *   - last document still being analysed
 *   - draft appointment with no prep summary yet
 *
 * Calm, premium, single-card. Renders nothing if there's no recent activity
 * — never noisy, never empty-state cluttery.
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Clock } from '@phosphor-icons/react';
import axios, { API } from '../../services/api';
import { useSWRCache } from '../../services/useSWRCache';

/** Pick the single most-recent resumable item from briefing payload. */
const pickContinueItem = (briefing) => {
  if (!briefing) return null;
  const candidates = [];
  // 1. Last chat (within 24h, has activity).
  const lc = briefing.last_chat;
  if (lc && lc.id && lc.last_activity) {
    const ageH = (Date.now() - new Date(lc.last_activity).getTime()) / 3600000;
    if (ageH <= 24) {
      candidates.push({
        kind: 'chat',
        ts: new Date(lc.last_activity).getTime(),
        eyebrow: 'Continue your last conversation',
        title: lc.title || 'Untitled conversation',
        sub: 'Pick up where you left off — Kindra still has the context.',
        href: `/chat?session=${lc.id}`,
      });
    }
  }
  // 2. Document still being processed.
  const procDocs = (briefing.recent_documents || []).filter(
    (d) => d && (d.extraction_status === 'processing' || d.extraction_status === 'queued'),
  );
  if (procDocs.length > 0) {
    const d = procDocs[0];
    const ts = new Date(d.created_at || d.uploaded_at || Date.now()).getTime();
    candidates.push({
      kind: 'doc-processing',
      ts,
      eyebrow: "Your report is being read",
      title: d.smart_label || d.filename || 'Recent upload',
      sub: 'Almost done — we’ll show the plain-English summary when it’s ready.',
      href: `/documents/${d.id}`,
    });
  }
  // 3. Recent appointment with no prep yet.
  const aps = briefing.upcoming || [];
  for (const a of aps) {
    if (!a || !a.id) continue;
    if (a.has_prep_summary) continue;
    const when = a.appointment_date || a.date;
    if (!when) continue;
    const ageDays = (new Date(when).getTime() - Date.now()) / 86400000;
    if (ageDays >= 0 && ageDays <= 7) {
      candidates.push({
        kind: 'appt-prep',
        ts: Date.now(),
        eyebrow: 'Continue your appointment prep',
        title: a.provider_name || a.title || 'Upcoming appointment',
        sub: 'Kindra can pull together a summary so the visit goes smoothly.',
        href: `/appointments/${a.id}`,
      });
      break;
    }
  }
  if (!candidates.length) return null;
  candidates.sort((a, b) => b.ts - a.ts);
  return candidates[0];
};

export const ContinueWhereYouLeftOff = ({ briefing }) => {
  const navigate = useNavigate();
  // Briefing might come in via prop OR from the SWR cache directly — both
  // paths supported so this card can render on Home (prop) or anywhere else
  // (cache).
  const { data: cached } = useSWRCache('/home/briefing', { ttlMs: 30_000, enabled: !briefing });
  const source = briefing || cached;
  const item = pickContinueItem(source);

  if (!item) return null;

  return (
    <motion.button
      type="button"
      onClick={() => navigate(item.href)}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32 }}
      className="w-full text-left rounded-2xl border border-[#ECE8E1] bg-white p-5 lg:p-6 hover:border-[#CFC8BB] hover:bg-[#FBFAF6] transition-colors"
      data-testid="continue-where-you-left-off"
      data-continue-kind={item.kind}
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-lg bg-[#235347]/10 flex items-center justify-center">
          <Clock size={16} className="text-[#235347]" weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] tracking-[0.18em] uppercase text-slate-400 font-medium">
            {item.eyebrow}
          </p>
          <h3
            className="text-[18px] sm:text-[20px] text-[#0E1C2E] leading-snug mt-1 truncate"
            style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
            title={item.title}
          >
            {item.title}
          </h3>
          <p className="text-[12.5px] text-slate-500 mt-1 leading-relaxed">{item.sub}</p>
        </div>
        <ArrowRight size={14} className="text-slate-400 mt-2 shrink-0" weight="bold" />
      </div>
    </motion.button>
  );
};

export default ContinueWhereYouLeftOff;
